/**
 * The break manifest contract.
 *
 * Transferred in shape from Chirp/shrike.html's BREAKS table. Gesture Synth
 * ships one verified CC0 re-recording from that crate.
 *
 * Provenance is part of the contract, not a comment. An entry cannot be
 *     enabled without a source and a licence string, and `validateManifest`
 *     enforces that — a break with no provenance is a bug, not a default.
 */

export interface BreakProvenance {
  /** Human-readable source title. */
  source: string;
  /** Canonical source page for the recording. */
  sourceUrl?: string;
  /** Recording creator/uploader. */
  creator?: string;
  /** The licence asserted for the recording. */
  licence: string;
  /** Lineage inside the user's private source repository. */
  lineage?: string;
  /** SHA-256 of the exact file committed here. */
  sha256?: string;
}

export interface BreakEntry {
  id: string;
  label: string;
  /** Path under public/, or null when the user has not supplied a file yet. */
  file: string | null;
  /** Source tempo, if known. null means "guess it from one bar". */
  bpm: number | null;
  /** Bars the file contains. The slicer chops ONE bar at a time. */
  bars: number;
  /** Fixed slice count per bar. 8 = eighth-note slices, the classic chop. */
  slices: number;
  enabled: boolean;
  provenance: BreakProvenance | null;
}

/**
 * The shipped Amen is a programmed/re-recorded performance, not the original
 * 1969 Winstons master. Its creator released it under CC0.
 */
export const BREAK_MANIFEST: BreakEntry[] = [
  {
    id: 'amen',
    label: 'AMEN REMAKE',
    file: 'audio/breaks/amen.mp3',
    bpm: 180,
    bars: 4,
    slices: 8,
    enabled: true,
    provenance: {
      source: 'AmenBreak_Remake_Compressed_Loop_180BPM',
      sourceUrl: 'https://freesound.org/people/JapanYoshiTheGamer/sounds/576562/',
      creator: 'JapanYoshiTheGamer',
      licence: 'Creative Commons CC0 1.0 Public Domain Dedication',
      lineage: 'Chirp/demo/breaks/01_AmenBreak_Remake_180BPM.mp3 at 6f4d80a',
      sha256: '1a157f175819ba009066b6382c388b3aceff11cc6dd1c0b54e1c204f012b4176',
    },
  },
];

export interface ManifestIssue {
  id: string;
  field: string;
  message: string;
}

/**
 * Pure validation. An enabled entry must carry a file AND full provenance;
 * slice counts must be a sane power-of-two-ish chop; ids must be unique.
 */
export function validateManifest(entries: readonly BreakEntry[]): ManifestIssue[] {
  const issues: ManifestIssue[] = [];
  const seen = new Set<string>();
  for (const e of entries) {
    if (!e.id) issues.push({ id: e.id, field: 'id', message: 'entry has no id' });
    if (seen.has(e.id)) issues.push({ id: e.id, field: 'id', message: 'duplicate id' });
    seen.add(e.id);
    if (e.slices < 1 || e.slices > 32 || !Number.isInteger(e.slices))
      issues.push({ id: e.id, field: 'slices', message: 'slices must be an integer 1..32' });
    if (e.bars < 1 || !Number.isInteger(e.bars))
      issues.push({ id: e.id, field: 'bars', message: 'bars must be a positive integer' });
    if (e.bpm !== null && (e.bpm < 40 || e.bpm > 300))
      issues.push({ id: e.id, field: 'bpm', message: 'bpm out of range 40..300' });
    if (e.enabled && !e.file)
      issues.push({ id: e.id, field: 'file', message: 'enabled entry has no file' });
    if (e.enabled && !e.provenance)
      issues.push({ id: e.id, field: 'provenance', message: 'enabled entry has no provenance — source and licence are required' });
    if (e.enabled && e.provenance && (!e.provenance.source.trim() || !e.provenance.licence.trim()))
      issues.push({ id: e.id, field: 'provenance', message: 'provenance needs both a source and a licence' });
  }
  return issues;
}

export const isPlayable = (e: BreakEntry): boolean =>
  e.enabled && !!e.file && !!e.provenance && validateManifest([e]).length === 0;

/**
 * The reconstruction pattern: every slice back at its own spot, in order. This
 * is ReCycle's REX default and Shrike's ↺ Reconstruct button — the sequence you
 * start from before you rearrange anything.
 */
export const reconstructPattern = (slices: number): number[] =>
  Array.from({ length: slices }, (_, i) => i);

/** Rotate the pattern — the cheapest interesting rearrangement. */
export function rotatePattern(pattern: readonly number[], by: number): number[] {
  const n = pattern.length;
  if (!n) return [];
  const k = ((by % n) + n) % n;
  return pattern.slice(k).concat(pattern.slice(0, k));
}

/** Every other slice replaced by the one before it — the stutter. */
export function stutterPattern(pattern: readonly number[]): number[] {
  return pattern.map((v, i) => (i % 2 === 1 ? pattern[i - 1] : v));
}
